// @workflow assistant.clarification
/**
 * A clarification's half-finished answer, kept in the store (coordinator ruling, 2026-10-14): the
 * stepper state (clarificationSteps.js) lives on the request record, `conversation.requests
 * [requestId].steps`, so opening History or Settings (which unmounts the composer and the panel)
 * loses no picks. It exists only while the request is pending: the reducer drops it when the
 * request is answered, dismissed, expires or its reply ends (clientRequests.settledRequest), and a
 * new chat starts a new conversation. Like a UI request's progress line, it never leaves the browser.
 *
 * A question the reply asks as text (textQuestion.js) has no request record and needs none: it is
 * one single-select question, answered by the pick itself.
 * Every function is immutable. No Vue or path-alias imports (node --test loads it directly).
 */
import { REQUEST_KIND, REQUEST_STATUS, DRAFT_KEY } from './clientRequests.js';
import { questionsOf } from './clarification.js';
import { clarificationPart } from './clarificationState.js';
import { startSteps, pickOption, withTypedText, goToStep, stepsView, submission, draftOf } from './clarificationSteps.js';

const pendingRequest = (c, requestId) => {
  const request = c && c.requests ? c.requests[requestId] : null;
  return request && request.kind === REQUEST_KIND.CLARIFICATION && request.status === REQUEST_STATUS.PENDING ? request : null;
};

/** The questions of a pending clarification, or null. */
const pendingQuestions = (c, requestId) => {
  if (!pendingRequest(c, requestId)) return null;
  const questions = questionsOf(clarificationPart(c, requestId));
  return questions.length ? questions : null;
};

/** The stored steps of a pending clarification; a fresh start when none are stored yet. */
export function clarificationStepsOf(c, requestId) {
  const request = pendingRequest(c, requestId);
  return (request && request[DRAFT_KEY]) || startSteps();
}

function withSteps(c, requestId, steps) {
  const request = pendingRequest(c, requestId);
  if (!request || request[DRAFT_KEY] === steps) return c;
  return { ...c, requests: { ...c.requests, [requestId]: { ...request, [DRAFT_KEY]: steps } } };
}

/** Applies one stepper change to the stored steps; anything but a pending clarification is left as it is. */
function update(c, requestId, change) {
  const questions = pendingQuestions(c, requestId);
  return questions ? withSteps(c, requestId, change(clarificationStepsOf(c, requestId), questions)) : c;
}

/**
 * A pick on the question on screen (clarificationSteps.pickOption).
 * @returns {{ conversation: object, submit: object|null }} `submit`: the answer to send now
 *   (a one-question single-select pick), else null
 */
export function withClarificationPick(c, requestId, optionId) {
  const questions = pendingQuestions(c, requestId);
  if (!questions) return { conversation: c, submit: null };
  const { steps, submit } = pickOption(clarificationStepsOf(c, requestId), questions, optionId);
  return { conversation: withSteps(c, requestId, steps), submit };
}

/** What the user typed answers the question on screen. */
export const withClarificationText = (c, requestId, text) => update(c, requestId, (steps, questions) => withTypedText(steps, questions, text));

/** Shows another question (Back, Next, ← →). */
export const withClarificationStep = (c, requestId, index) => update(c, requestId, (steps, questions) => goToStep(steps, questions, index));

/**
 * What the panel shows (clarificationSteps.stepsView) plus `draft`, the picks and text of the
 * question on screen; null when the clarification is not pending.
 */
export function clarificationStepsView(c, requestId) {
  const questions = pendingQuestions(c, requestId);
  if (!questions) return null;
  const steps = clarificationStepsOf(c, requestId);
  return { ...stepsView(steps, questions), draft: draftOf(steps, questions) };
}

/** The answer Submit sends, or null while a question has none (or the clarification is not pending). */
export function clarificationSubmission(c, requestId) {
  const questions = pendingQuestions(c, requestId);
  return questions ? submission(clarificationStepsOf(c, requestId), questions) : null;
}
